/**
 * Time-advected wind samples — turning a few current readings into a few hundred historical ones.
 *
 * The field used to be interpolated from ONE instant: the current markwind timestep plus one 20s-averaged sample
 * per boat, so roughly a dozen points. Everything else was already in memory and thrown away — `markwindData`
 * holds every timestep for the day, and the boat row catalogs hold minutes of rows.
 *
 * This module uses the last few minutes of both, and relocates each reading to where the air it measured is NOW:
 *
 *     p' = p + alpha * V̄ * (t_now - t_obs)         advect along the mean flow
 *     w  = w_0 * exp(-(t_now - t_obs) / tau)       and trust it less the older it is
 *
 * That is Taylor's frozen-turbulence hypothesis: over a few minutes and a few kilometres, gusts and lulls are
 * carried downwind roughly intact. `alpha` is below 1 because surface features travel a little slower than the
 * measured wind. The payoff is structure the marks alone cannot express — a gust measured at the top mark a minute
 * ago now appears mid-course, and a boat's track becomes a filament of readings rather than a single dot.
 *
 * Everything here is pure and unit-tested. The advection velocity is the CURRENT mean flow passed in by the
 * caller, never the local interpolated wind, so the field cannot feed back on its own transport.
 */

import type { WindSample } from "./windFieldInterpolate";
import type { UV } from "./windParticleField";
import { calculateDestination, normalizeAngle } from "../mapUtils";
import {
  BOAT_WIND_MIN_BSP_KPH,
  newWindAcc,
  resolveFields,
  rowMs,
  speedScaleToKph,
  windAccAdd,
  windAccFinish,
  type BoatWindFields,
  type WindAcc,
} from "../../../../../utils/boatWindSamples";

/** A wind sample tagged with the time it was OBSERVED, which is what advection and decay need. */
export interface TimedWindSample extends WindSample {
  tMs: number;
}

/** How far back to reach for readings. Set to 0 to disable history entirely (single instant, no advection). */
export const WIND_HISTORY_MS = 300_000;
/** Boat rows are averaged into one sample per bin of this length, so a track becomes a chain of readings. */
export const BOAT_BIN_MS = 10_000;
/**
 * Fraction of the mean wind speed at which features are transported. Surface gust structures move a little slower
 * than the wind that is measured in them; 0.8 is the usual working value.
 */
export const ADVECTION_ALPHA = 0.8;
/** Trust half-life-ish: a reading's weight decays by e over this long, so 5-minute-old data barely votes. */
export const DECAY_TAU_MS = 180_000;
/**
 * Hard ceiling on samples handed to the interpolator, newest kept. The IDW pass is O(nodes x samples), measured at
 * ~16ms for 450 samples and ~35ms for 800 on an 80x80 grid, so this bounds the worst case regardless of how dense
 * the data or how many boats are selected.
 */
export const MAX_SAMPLES = 800;

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

interface MarkwindTimestepLike {
  DATETIME: string;
  MARKS?: Array<{ NAME?: string; LAT: string; LON: string; TWS?: string; TWD?: string }>;
}

function tsMs(datetime: string | undefined): number {
  if (!datetime) return NaN;
  const ms = new Date(datetime).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

/** Compass bearing (deg) the wind is blowing TOWARD, from a "came from" wind vector. */
export function downwindBearingDeg(uv: UV): number {
  const from = Math.atan2(uv.u, uv.v) * RAD2DEG;
  return normalizeAngle(((from % 360) + 360) % 360 + 180);
}

/**
 * Every mark reading in the trailing window, one sample per mark per timestep.
 *
 * Free data: the whole day's timesteps are already in memory, and marks are stationary and grade-checked, so they
 * keep full weight. With `historyMs = 0` this returns only the newest timestep at or before `endMs`, which is the
 * single-instant behaviour the overlay had before.
 */
export function collectMarkHistorySamples(
  timesteps: MarkwindTimestepLike[] | null | undefined,
  opts: { endMs: number; historyMs?: number }
): TimedWindSample[] {
  if (!Array.isArray(timesteps) || timesteps.length === 0) return [];
  const historyMs = Math.max(0, opts.historyMs ?? WIND_HISTORY_MS);
  const endMs = opts.endMs;
  if (!Number.isFinite(endMs)) return [];

  /**
   * The single nearest timestep at or before `endMs`, falling back to the newest overall when the playhead sits
   * before all of them. This is the behaviour the overlay's old `pickTimestep` had, and it is the SAFETY NET
   * below: a field that draws the nearest reading is always better than a field that vanishes.
   */
  const nearest = (): TimedWindSample[] => {
    let best = -Infinity;
    let newest = -Infinity;
    for (const ts of timesteps) {
      const ms = tsMs(ts?.DATETIME);
      if (!Number.isFinite(ms)) continue;
      if (ms > newest) newest = ms;
      if (ms <= endMs && ms > best) best = ms;
    }
    const pick = best > -Infinity ? best : newest;
    if (!Number.isFinite(pick)) return [];
    return markSamplesInRange(timesteps, pick, pick);
  };

  if (historyMs === 0) return nearest();

  const windowed = markSamplesInRange(timesteps, endMs - historyMs, endMs);
  // NEVER return nothing while there is data to draw. The window can miss the data entirely — a playhead that has
  // not been initialised to the dataset, a scrub past the end of the day, a date whose markwind starts later —
  // and the overlay treats an empty sample set as "clear the field", which would blank the map.
  return windowed.length > 0 ? windowed : nearest();
}

function markSamplesInRange(
  timesteps: MarkwindTimestepLike[],
  startMs: number,
  endMs: number
): TimedWindSample[] {
  const out: TimedWindSample[] = [];
  for (const ts of timesteps) {
    const ms = tsMs(ts?.DATETIME);
    if (!Number.isFinite(ms) || ms < startMs || ms > endMs) continue;
    for (const m of ts.MARKS || []) {
      const lat = parseFloat(m.LAT);
      const lng = parseFloat(m.LON);
      const tws = m.TWS != null ? parseFloat(String(m.TWS)) : NaN;
      const twd = m.TWD != null ? parseFloat(String(m.TWD)) : NaN;
      if (isNaN(lat) || isNaN(lng) || isNaN(tws) || isNaN(twd)) continue;
      out.push({ lng, lat, tws, twd, tMs: ms });
    }
  }
  return out;
}

/**
 * One averaged sample per boat per time bin across the trailing window.
 *
 * A SINGLE pass over each boat's rows, accumulating into per-bin accumulators — the same averaging rules as
 * `collectBoatWindSamples` (circular mean TWD, plain mean TWS/BSP, latest fix for position, moving-boat gate),
 * because it is literally the same accumulator. Walking the rows once matters: this runs on the main thread and
 * the row catalogs can hold minutes of multi-Hz data.
 */
export function collectBoatHistorySamples(
  rowsBySource: Map<number, any[]> | null | undefined,
  fields: BoatWindFields,
  opts: { endMs: number; historyMs?: number; binMs?: number; minBspKph?: number; weight?: number }
): TimedWindSample[] {
  if (!rowsBySource || rowsBySource.size === 0) return [];
  const historyMs = Math.max(0, opts.historyMs ?? WIND_HISTORY_MS);
  const binMs = Math.max(1000, opts.binMs ?? BOAT_BIN_MS);
  const minBspKph = opts.minBspKph ?? BOAT_WIND_MIN_BSP_KPH;
  const endMs = opts.endMs;
  if (!Number.isFinite(endMs) || historyMs === 0) return [];

  const startMs = endMs - historyMs;
  const binCount = Math.max(1, Math.ceil(historyMs / binMs));
  const out: TimedWindSample[] = [];

  for (const rows of rowsBySource.values()) {
    if (!Array.isArray(rows) || rows.length === 0) continue;
    const f = resolveFields(rows, fields);
    const twsScale = speedScaleToKph(f.tws);
    const bspScale = speedScaleToKph(f.bsp);

    const accs: Array<WindAcc | undefined> = new Array(binCount);
    for (const row of rows) {
      const ms = rowMs(row);
      if (!Number.isFinite(ms) || ms < startMs || ms > endMs) continue;
      let bin = Math.floor((ms - startMs) / binMs);
      if (bin < 0) bin = 0;
      if (bin >= binCount) bin = binCount - 1;
      let acc = accs[bin];
      if (!acc) {
        acc = newWindAcc();
        accs[bin] = acc;
      }
      windAccAdd(acc, row, ms, f, twsScale, bspScale);
    }

    for (let bin = 0; bin < binCount; bin++) {
      const acc = accs[bin];
      if (!acc) continue;
      const avg = windAccFinish(acc, minBspKph);
      if (!avg) continue;
      out.push({
        lng: avg.lng,
        lat: avg.lat,
        tws: avg.tws,
        twd: avg.twd,
        weight: opts.weight,
        // The bin's own position fix time is the observation time that matters for advection.
        tMs: acc.posMs,
      });
    }
  }
  return out;
}

/**
 * Keep the newest `max` samples. Returns the dropped count so the caller can report it — a silently truncated
 * field would read as "we used everything" when it did not.
 */
export function capSamples<T extends { tMs: number }>(
  samples: T[],
  max: number = MAX_SAMPLES
): { samples: T[]; dropped: number } {
  if (samples.length <= max) return { samples, dropped: 0 };
  const sorted = [...samples].sort((a, b) => b.tMs - a.tMs);
  return { samples: sorted.slice(0, max), dropped: samples.length - max };
}

/**
 * Move each sample downwind to where the air it measured is now, and decay its weight with age.
 *
 * `meanUV` is the CURRENT mean flow (a "came from" wind vector, km/h). Samples with `tMs >= endMs` are left where
 * they are. Displacements under a metre are skipped so the newest readings keep their exact positions.
 */
export function advectSamples(
  samples: TimedWindSample[],
  meanUV: UV | null | undefined,
  opts: { endMs: number; alpha?: number; tauMs?: number }
): WindSample[] {
  const alpha = opts.alpha ?? ADVECTION_ALPHA;
  const tauMs = Math.max(1, opts.tauMs ?? DECAY_TAU_MS);
  const endMs = opts.endMs;
  const meanMps = meanUV ? Math.hypot(meanUV.u, meanUV.v) / 3.6 : 0;
  const downwind = meanUV ? downwindBearingDeg(meanUV) : 0;

  const out: WindSample[] = [];
  for (const s of samples) {
    if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) continue;
    const ageMs = Number.isFinite(s.tMs) ? Math.max(0, endMs - s.tMs) : 0;
    const weight = (s.weight ?? 1) * Math.exp(-ageMs / tauMs);
    if (!(weight > 0)) continue;

    let lat = s.lat;
    let lng = s.lng;
    const distM = alpha * meanMps * (ageMs / 1000);
    if (distM > 1) {
      const moved = calculateDestination(s.lat, s.lng, distM, downwind);
      if (moved) {
        lat = moved.lat;
        lng = moved.lng;
      }
    }
    out.push({ lng, lat, tws: s.tws, twd: s.twd, weight });
  }
  return out;
}

/** Mean wind vector over the samples' own weights — used as the advection velocity and the anisotropy axis. */
export function meanOfTimedSamples(samples: TimedWindSample[]): UV | null {
  let sumW = 0;
  let sumU = 0;
  let sumV = 0;
  for (const s of samples) {
    if (!Number.isFinite(s.tws) || !Number.isFinite(s.twd)) continue;
    const w = Number.isFinite(s.weight as number) && (s.weight as number) > 0 ? (s.weight as number) : 1;
    const r = s.twd * DEG2RAD;
    sumU += s.tws * Math.sin(r) * w;
    sumV += s.tws * Math.cos(r) * w;
    sumW += w;
  }
  if (sumW <= 0) return null;
  return { u: sumU / sumW, v: sumV / sumW };
}
